/**
 * The PERSON's own `ocl_` access-token RPC definitions (OL-114).
 *
 * An access token is a person's own bearer credential: it lets an agent act
 * **as them**, which makes this group actor-scoped in the strongest sense the
 * repo has — the same rule `orcaloops-credentials.rpc.ts` already states.
 *
 * **No payload here carries a `personId`.** The subject is always
 * `LoopActor.person.id`, resolved by {@link LoopActorMiddleware}. An
 * actor-scoped group that accepted a subject id would be one missing check away
 * from letting a member list a colleague's credentials.
 *
 * It carries no `customerId` selector either, and that is deliberate rather than
 * an omission: a token belongs to a PERSON, not to a customer (the
 * `orcaloops.access_tokens.customer_id` column is the token's own pin), so
 * there is no customer scope for a selector to select — `listForPerson` is the
 * whole query. The three sibling groups, whose rows ARE customer-owned, all take
 * the optional selector and put it through `resolveActorCustomer`.
 *
 * This group is pre-created by the seam issue with a single `accessTokens.list`
 * rpc so the wiring is already in place. The mint/revoke issue ADDS
 * `accessTokens.mint` and `accessTokens.revoke` to this same group; it does not
 * create a new one.
 *
 * **The plaintext appears in exactly ONE schema in this codebase**, and it is
 * {@link MintAccessTokenResult}`.token` — the value is shown once, at mint, and
 * is unrecoverable afterwards because only `hashToken(plaintext)` is persisted.
 * No READ path carries it: `accessTokens.list` and `accessTokens.revoke` both
 * answer with `AccessToken.json`, which has no `tokenHash` key either, because
 * the model declares that column `Model.Sensitive`. Both of those omissions are
 * mechanical rather than a matter of handler discipline — there is no key in the
 * schema for a future edit to fill in.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { AccessToken } from "@fm/db/models/AccessToken";
import { Schema } from "effect";

import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * Prefix carried by every minted plaintext access token.
 *
 * Its twin is the private `ACCESS_TOKEN_PREFIX` in `rpc/loop-actor.middleware.ts`,
 * which is the prefix that middleware branches on to send a bearer down the
 * access-token path instead of the Clerk one. Each side declares the literal and
 * each comment names the other — the same arrangement `admin/auth.router.ts`
 * (`SERVICE_TOKEN_PREFIX`) has with `admin.middleware.ts` — because this file is
 * client-safe and must not import the server-side middleware. There is no third
 * home for the literal; do not invent one.
 *
 * @since 1.0.0
 */
export const ACCESS_TOKEN_PREFIX = "ocl_";

/**
 * Payload for `accessTokens.mint`.
 *
 * It carries no `personId`: the owner is always `LoopActor.person.id`. The
 * optional `customerId` is the one selector on this group, and it is a selector
 * among the CALLER'S OWN memberships, never an authority — a pin can only narrow
 * a token's reach, never widen it.
 *
 * @since 1.0.0
 */
export const MintAccessTokenInput = Schema.Struct({
  /** Human label, so the owner can recognise the token in the list and revoke it. */
  label: Schema.String,
  /** Pin the token to ONE of the caller's customers. Absent = it spans their memberships. */
  customerId: Schema.optional(Schema.String),
  /** Absent = the token does not expire; revocation is the control. */
  expiresInDays: Schema.optional(Schema.Int),
});

/**
 * @since 1.0.0
 */
export type MintAccessTokenInput = Schema.Schema.Type<typeof MintAccessTokenInput>;

/**
 * The ONLY response in this codebase that ever carries the plaintext token.
 * Shown once, never persisted, unrecoverable afterwards — the row keeps only the
 * SHA-256 digest, and no read path can serve it back.
 *
 * @since 1.0.0
 */
export const MintAccessTokenResult = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  /** The plaintext `ocl_` bearer. This is the only place it exists after mint. */
  token: Schema.String,
  /** `null` when the token has no expiry. */
  expiresAt: Schema.NullOr(Schema.DateTimeUtc),
});

/**
 * @since 1.0.0
 */
export type MintAccessTokenResult = Schema.Schema.Type<typeof MintAccessTokenResult>;

/**
 * Payload for `accessTokens.revoke` — the token row's id, and nothing else. The
 * owning person comes from the actor and is applied inside the accessor, so
 * "not yours" and "no such token" are the same answer.
 *
 * @since 1.0.0
 */
export const RevokeAccessTokenInput = Schema.Struct({ id: Schema.String });

/**
 * @since 1.0.0
 */
export type RevokeAccessTokenInput = Schema.Schema.Type<typeof RevokeAccessTokenInput>;

/**
 * The person's own access-token RPC group. Every member carries
 * {@link LoopActorMiddleware}.
 *
 * The wire row is `AccessToken.json` from `@fm/db/models/AccessToken` — never
 * redeclared here. It carries NO `tokenHash` key at all, because the model
 * declares that column `Model.Sensitive`; that omission is mechanical, so a
 * future handler edit cannot leak the digest even by accident.
 *
 * @since 1.0.0
 */
export class AccessTokenRpcs extends RpcGroup.make(
  Rpc.make("accessTokens.list", {
    payload: Schema.Struct({}),
    success: Schema.Array(AccessToken.json),
    error: Schema.Union(Unauthorized, DatabaseError),
  }).middleware(LoopActorMiddleware),
  /**
   * Mint the caller's own `ocl_` bearer credential. **Clerk-only**: a
   * token-authed actor is refused, so a leaked agent credential cannot
   * self-extend into a second one that outlives the revocation of the first.
   */
  Rpc.make("accessTokens.mint", {
    payload: MintAccessTokenInput,
    success: MintAccessTokenResult,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError, DatabaseError),
  }).middleware(LoopActorMiddleware),
  /**
   * Retire one of the caller's own tokens. Clerk-only with one deliberate
   * exception: a token-authed actor may revoke the token it authenticated with,
   * and nothing else — the switch an agent that notices its own credential is
   * compromised should pull, without needing a browser.
   */
  Rpc.make("accessTokens.revoke", {
    payload: RevokeAccessTokenInput,
    success: AccessToken.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(LoopActorMiddleware),
) {}
